import { useContext, useMemo, useState, useEffect } from "react";
import { CartContext } from "../../App";
import PricingService, {
  CartPricingSummary,
  CouponData,
} from "../PricingService";

/**
 * Custom hook that provides pricing calculations and data
 * Automatically recalculates when cart items, bundle info, or coupon data changes
 */
export const usePricing = (paymentType: "prepaid" | "cod" = "cod") => {
  const { checkOutItems, bundleInfo } = useContext(CartContext);
  const [couponUpdateTrigger, setCouponUpdateTrigger] = useState(0);

  // Force re-calculation when coupon changes
  useEffect(() => {
    const handleStorageChange = () => {
      setCouponUpdateTrigger((prev) => prev + 1);
    };

    // Listen for storage changes from other tabs
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom coupon update events
    window.addEventListener("couponUpdated", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("couponUpdated", handleStorageChange);
    };
  }, []);

  // Memoize the complete cart pricing calculation
  const cartPricing = useMemo((): CartPricingSummary => {
    return PricingService.calculateCartPricing(
      checkOutItems,
      bundleInfo,
      undefined,
      paymentType
    );
  }, [checkOutItems, bundleInfo, paymentType, couponUpdateTrigger]);

  // Memoize coupon data with dependency on session storage changes
  const couponData = useMemo((): CouponData => {
    return PricingService.getCouponData();
  }, [couponUpdateTrigger]); // Re-calculate when coupon changes

  // Helper functions that use the pricing service
  const getDisplayPrice = (itemId: number): number => {
    const item = checkOutItems.find((item) => item.id === itemId);
    if (!item) return 0;
    return PricingService.getDisplayPrice(item, bundleInfo, checkOutItems);
  };

  const shouldShowBundlePrice = (itemId: number): boolean => {
    return PricingService.shouldShowBundlePrice(
      itemId,
      bundleInfo,
      checkOutItems
    );
  };

  const isBundlePricingActive = (): boolean => {
    return PricingService.isBundlePricingActive(bundleInfo, checkOutItems);
  };

  const getBundleParams = () => {
    return PricingService.getBundleParams(bundleInfo, checkOutItems);
  };

  const calculateCurrentAmount = (): number => {
    return PricingService.calculateCurrentAmount(
      checkOutItems,
      bundleInfo,
      paymentType
    );
  };

  const getAppliedCouponCode = (): string | undefined => {
    return PricingService.getAppliedCouponCode();
  };

  const formatPrice = (price: number): string => {
    return PricingService.formatPrice(price);
  };

  const calculateCODCharges = (): number => {
    return PricingService.calculateCODCharges(checkOutItems.length);
  };

  const calculateOrderTotal = (paymentType: "prepaid" | "cod"): number => {
    return PricingService.calculateOrderTotal(
      cartPricing.subtotal.finalPrice,
      paymentType,
      checkOutItems.length
    );
  };

  const getPrepaidDiscount = (): number => {
    return cartPricing.subtotal.prepaidDiscount;
  };

  const forceUpdate = (): void => {
    setCouponUpdateTrigger((prev) => prev + 1);
  };

  return {
    // Pricing data
    cartPricing,
    couponData,

    // Individual item pricing
    getDisplayPrice,
    shouldShowBundlePrice,

    // Bundle pricing
    isBundlePricingActive,
    getBundleParams,

    // Coupon data
    getAppliedCouponCode,

    // Calculations
    calculateCurrentAmount,
    calculateCODCharges,
    calculateOrderTotal,
    getPrepaidDiscount,

    // Utilities
    formatPrice,
    forceUpdate,

    // Raw data for advanced usage
    checkOutItems,
    bundleInfo,
  };
};

export default usePricing;
